/*
"Comparer cette espèce avec…" picker.

Triggered by a long-press on an Arboretum card. Surfaces a bottom
sheet listing every OTHER species the user has captured, ranked
most-recent-first. Tap one → navigate to the comparison screen with
both speciesIndex values in the path.

Keeping the picker scoped to *captured* species avoids dumping the
full 909-species catalogue into the picker — comparison only makes
sense between species the user has touched, since the comparison
screen leans on personal photos for half its content.
*/

import { listCaptures } from '../../data/captureRepository'
import { speciesNameFor, type SpeciesName } from '../../data/speciesIndex'

const kSheetId: string = 'compare-picker'

export interface ComparePickerCallbacks
{
	onPick: (inOtherSk: number) => void
}

interface OtherSpecies
{
	sk: number
	name: SpeciesName | null
	mostRecent: number  // capture timestamp, used for the sort
}

/*
Open the picker for the given source species. Builds + appends the
sheet, focuses for keyboard accessibility, and returns a close
function. The sheet hides + removes itself on selection or on the
× / backdrop tap.
*/
export async function openComparePicker(
	inSourceSk: number,
	inSourceLabel: string,
	inCallbacks: ComparePickerCallbacks,
): Promise<() => void>
{
	const vExisting = document.getElementById(kSheetId)
	if (vExisting) vExisting.remove()

	const vRoot = document.createElement('div')
	vRoot.id = kSheetId
	vRoot.className = 'compare-picker'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-modal', 'true')
	vRoot.innerHTML = `
		<div class="compare-picker-backdrop"></div>
		<div class="compare-picker-panel">
			<header class="compare-picker-header">
				<div class="compare-picker-title-block">
					<div class="compare-picker-eyebrow">Comparer ${escapeHtml(inSourceLabel)} avec…</div>
					<div class="compare-picker-hint">Choisis une espèce de ton arboretum.</div>
				</div>
				<button class="compare-picker-close" type="button" aria-label="Fermer">×</button>
			</header>
			<div class="compare-picker-list" id="compare-picker-list">
				<div class="compare-picker-loading">Chargement…</div>
			</div>
		</div>
	`
	document.body.appendChild(vRoot)
	requestAnimationFrame(() => vRoot.setAttribute('data-open', 'true'))

	let vClosed: boolean = false  // single-fire guard for the dispose path
	const vClose = (): void => {
		if (vClosed) return
		vClosed = true
		vRoot.setAttribute('data-open', 'false')
		document.removeEventListener('keydown', vOnKey)
		window.setTimeout(() => vRoot.remove(), 220)
	}

	const vOnKey = (inEv: KeyboardEvent): void => { if (inEv.key === 'Escape') vClose() }
	document.addEventListener('keydown', vOnKey)

	vRoot.querySelector<HTMLButtonElement>('.compare-picker-close')?.addEventListener('click', vClose)
	vRoot.querySelector<HTMLElement>('.compare-picker-backdrop')?.addEventListener('click', vClose)

	// Populate asynchronously so the sheet animates in immediately and
	// the captures read happens behind it.
	try
	{
		const vAllCaptures = await listCaptures()
		const vSeen = new Map<number, number>()  // sk -> most recent timestamp, excluding the source species
		for (const vCap of vAllCaptures)
		{
			if (vCap.speciesIndex === inSourceSk) continue
			const vPrev = vSeen.get(vCap.speciesIndex)
			if (vPrev === undefined || vCap.timestamp > vPrev) vSeen.set(vCap.speciesIndex, vCap.timestamp)
		}
		const vOthers: OtherSpecies[] = [...vSeen.entries()]
			.map(([vSk, vTs]) => ({ sk: vSk, name: speciesNameFor(vSk), mostRecent: vTs }))
			.sort((inA, inB) => inB.mostRecent - inA.mostRecent)

		const vList = vRoot.querySelector<HTMLElement>('#compare-picker-list')
		if (!vList) return vClose

		if (vOthers.length === 0)
		{
			vList.innerHTML = `<div class="compare-picker-empty">Capture au moins une autre espèce pour pouvoir comparer.</div>`
			return vClose
		}

		vList.innerHTML = vOthers.map((inO) => {
			const vTitle = inO.name?.commonName ?? formatLatin(inO.name)
			const vSubtitle = inO.name?.commonName ? formatLatin(inO.name) : ''
			return `
				<button class="compare-picker-row" type="button" data-sk="${inO.sk}">
					<span class="compare-picker-glyph" aria-hidden="true">🌿</span>
					<span class="compare-picker-row-body">
						<span class="compare-picker-row-title">${escapeHtml(vTitle)}</span>
						${vSubtitle ? `<span class="compare-picker-row-subtitle"><em>${escapeHtml(vSubtitle)}</em></span>` : ''}
					</span>
				</button>
			`
		}).join('')

		vList.querySelectorAll<HTMLElement>('.compare-picker-row').forEach((inRow) => {
			inRow.addEventListener('click', () => {
				const vSk = Number(inRow.dataset.sk)
				if (!Number.isFinite(vSk)) return
				vClose()
				inCallbacks.onPick(vSk)
			})
		})
	}
	catch (vErr)
	{
		const vList = vRoot.querySelector<HTMLElement>('#compare-picker-list')
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur'
		if (vList) vList.innerHTML = `<div class="compare-picker-empty">⚠ ${escapeHtml(vMsg)}</div>`
	}

	return vClose
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
